import { useState } from "react"

import { useMsal } from "@azure/msal-react"
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { PrismClaimsPage, PrismForgetState, PrismMemoryClaim } from "@/api/memory"
import type { InfiniteData } from "@tanstack/react-query"

import { getToken } from "@/api/auth-api"
import {
  deleteAllImportedPrismMemoryApi,
  forgetPrismEventApi,
  getPrismClaimsApi,
  getPrismConversationTitleApi,
  getPrismForgetStatusApi,
  getPrismPauseApi,
  requestPrismForgetApi,
  retractPrismClaimApi,
  setPrismPauseApi,
} from "@/api/memory"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"

const KEY = ["prism-memory"] as const
const PREFIX = "personalization.memoryPanel.prism"

function ChatLabel({ uid }: { uid: string }) {
  const { t } = useTranslation()
  const { instance } = useMsal()
  const imported = uid.startsWith("imp~")
  const profile = uid.startsWith("prf~")
  const title = useQuery({
    queryKey: [...KEY, "title", uid],
    queryFn: async () => getPrismConversationTitleApi(uid, (await getToken(instance))?.accessToken),
    enabled: !imported && !profile,
    retry: false,
  })
  let label = title.data ?? t(`${PREFIX}.deletedChat`)
  if (imported) label = t(`${PREFIX}.importedLabel`)
  else if (profile) label = t(`${PREFIX}.profileLabel`)
  else if (title.isError) label = t(`${PREFIX}.errors.unavailable`)
  else if (title.isPending) label = t("common.loading")
  return <h4 className="text-sm font-medium break-words">{label}</h4>
}

type Action =
  | { kind: "claim"; claim: PrismMemoryClaim }
  | { kind: "chat"; uid: string }
  | { kind: "imported" }

function withoutTarget(old: InfiniteData<PrismClaimsPage> | undefined, action: Action) {
  return (
    old && {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        claims: page.claims.filter((item) => {
          if (action.kind === "imported") return !item.imported
          if (action.kind === "chat")
            return !item.conversation_uids.every((uid) => uid === action.uid)
          return !item.group_claim_uids.some((uid) => action.claim.group_claim_uids.includes(uid))
        }),
      })),
    }
  )
}

/** Claims are already R-48-filtered by HAI; local hiding closes the refetch window after a control. */
export function PrismClaimsSection({ active }: { active: boolean }) {
  const { t } = useTranslation()
  const { instance } = useMsal()
  const client = useQueryClient()
  const [confirming, setConfirming] = useState<Action | null>(null)
  const [hiddenClaims, setHiddenClaims] = useState<Set<string>>(new Set())
  const [hiddenChats, setHiddenChats] = useState<Set<string>>(new Set())
  const [importsHidden, setImportsHidden] = useState(false)
  const [localStates, setLocalStates] = useState<Map<string, PrismForgetState>>(new Map())
  const token = async () => (await getToken(instance))?.accessToken
  const claims = useInfiniteQuery({
    queryKey: [...KEY, "claims"],
    queryFn: async ({ pageParam }) => getPrismClaimsApi(await token(), pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    enabled: active,
    retry: false,
  })
  const forgets = useQuery({
    queryKey: [...KEY, "forgets"],
    queryFn: async () => getPrismForgetStatusApi(await token()),
    enabled: active,
    retry: false,
  })
  const pause = useQuery({
    queryKey: [...KEY, "pause"],
    queryFn: async () => getPrismPauseApi(await token()),
    enabled: active,
    retry: false,
  })
  const toggle = useMutation({
    mutationFn: async (paused: boolean) => setPrismPauseApi(paused, await token()),
    onSuccess: (result) => client.setQueryData([...KEY, "pause"], result),
    onError: () => toast.error(t(`${PREFIX}.errors.unavailable`)),
  })
  const remove = useMutation({
    onMutate: async (action: Action) => {
      // Stop a pre-control response from repopulating any consumer's cache.
      await client.cancelQueries({ queryKey: [...KEY, "claims"] })
      client.setQueriesData<InfiniteData<PrismClaimsPage>>(
        { queryKey: [...KEY, "claims"] },
        (old) => withoutTarget(old, action)
      )
    },
    mutationFn: async (action: Action) => {
      const auth = await token()
      if (action.kind === "imported") return deleteAllImportedPrismMemoryApi(auth)
      if (action.kind === "chat") return requestPrismForgetApi(action.uid, auth)
      const item = action.claim
      return item.event_target
        ? forgetPrismEventApi(item.event_target, auth)
        : retractPrismClaimApi(item.group_claim_uids, item.conversation_uids, auth)
    },
    onSuccess: (result, action) => {
      if (result.state !== "none") {
        if (action.kind === "imported") setImportsHidden(true)
        else if (action.kind === "chat") setHiddenChats((old) => new Set([...old, action.uid]))
        else setHiddenClaims((old) => new Set([...old, ...action.claim.group_claim_uids]))
      }
      let id = "imported"
      if (action.kind === "chat") id = action.uid
      else if (action.kind === "claim") id = action.claim.claim_uid
      setLocalStates((old) => new Map([...old, [id, result.state]]))
      toast.info(
        t(
          result.state === "confirmed" ? `${PREFIX}.toasts.confirmed` : `${PREFIX}.toasts.requested`
        )
      )
    },
    onError: (error) => {
      if ("code" in error && error.code === "no_open_stream")
        void client.invalidateQueries({ queryKey: [...KEY, "claims"] })
      toast.error(
        t(
          "code" in error && error.code === "restore_reconcile_pending"
            ? `${PREFIX}.toasts.restorePending`
            : `${PREFIX}.toasts.failed`
        )
      )
    },
    onSettled: () => {
      setConfirming(null)
      void client.invalidateQueries({ queryKey: KEY })
      void client.invalidateQueries({ queryKey: ["home-context"] })
    },
  })
  if (!active) return null
  const states = new Map(
    (forgets.data?.forgets ?? []).map((item) => [item.conversation_uid, item.state])
  )
  for (const [uid, state] of localStates) states.set(uid, state)
  const visible = (
    claims.isError ? [] : (claims.data?.pages.flatMap((page) => page.claims) ?? [])
  ).filter(
    (item) =>
      !item.group_claim_uids.some((uid) => hiddenClaims.has(uid)) &&
      !(importsHidden && item.imported) &&
      !item.conversation_uids.every(
        (uid) => hiddenChats.has(uid) || (states.has(uid) && states.get(uid) !== "none")
      )
  )
  const groups = new Map<string, PrismMemoryClaim[]>()
  // Imported memory remains available even when its events are not derived yet.
  groups.set(
    "imported",
    visible.filter((item) => item.imported)
  )
  for (const item of visible) {
    if (item.imported && item.conversation_uids.every((uid) => uid.startsWith("imp~"))) continue
    for (const uid of item.conversation_uids) {
      if (
        uid.startsWith("imp~") ||
        hiddenChats.has(uid) ||
        (states.has(uid) && states.get(uid) !== "none")
      )
        continue
      groups.set(uid, [...(groups.get(uid) ?? []), item])
    }
  }
  for (const uid of states.keys())
    if (uid !== "imported" && !localStates.has(uid) && !uid.startsWith("imp~"))
      groups.set(uid, groups.get(uid) ?? [])
  const status = (id: string) =>
    states.has(id) ? (
      <Badge className="max-w-full text-left whitespace-normal" variant="secondary">
        {t(`${PREFIX}.state.${states.get(id)}`)}
      </Badge>
    ) : null
  const confirmation = {
    claim: {
      title: `${PREFIX}.deleteClaimTitle`,
      description: `${PREFIX}.deleteClaimDescription`,
      confirm: "personalization.memoryPanel.actions.deleteMemory",
    },
    imported: {
      title: `${PREFIX}.deleteAllImported`,
      description: `${PREFIX}.deleteImportedDescription`,
      confirm: `${PREFIX}.deleteAllImported`,
    },
    chat: {
      title: `${PREFIX}.confirm.title`,
      description: `${PREFIX}.chatForgetDescription`,
      confirm: `${PREFIX}.confirm.confirm`,
    },
  }[confirming?.kind ?? "chat"]
  return (
    <section aria-label={t(`${PREFIX}.claimsTitle`)} className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-sm font-medium">{t(`${PREFIX}.claimsTitle`)}</h3>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => void client.invalidateQueries({ queryKey: KEY })}
        >
          {t(`${PREFIX}.refresh`)}
        </Button>
      </div>
      <div className="flex items-center justify-between gap-4">
        <Label htmlFor="prism-memory-pause">{t(`${PREFIX}.pause`)}</Label>
        <Switch
          checked={pause.data?.paused ?? false}
          disabled={!pause.isSuccess || toggle.isPending}
          id="prism-memory-pause"
          onCheckedChange={(paused) => toggle.mutate(paused)}
        />
      </div>
      {(claims.isError || pause.isError || forgets.isError) && (
        <p className="text-muted-foreground text-sm" role="status">
          {t(`${PREFIX}.errors.unavailable`)}
        </p>
      )}
      {claims.isPending && <p role="status">{t("common.loading")}</p>}
      {forgets.data?.has_more && <p role="status">{t(`${PREFIX}.statusTruncated`)}</p>}
      {[...groups].map(([uid, items]) => (
        <Card key={uid} data-testid={`prism-conversation-${uid}`}>
          <CardContent className="space-y-3 p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 flex-1 space-y-1">
                {uid === "imported" ? (
                  <h4 className="text-sm font-medium">{t(`${PREFIX}.importedLabel`)}</h4>
                ) : (
                  <ChatLabel uid={uid} />
                )}
                {status(uid)}
              </div>
              {states.get(uid) !== "confirmed" && (
                <Button
                  disabled={remove.isPending}
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    setConfirming(uid === "imported" ? { kind: "imported" } : { kind: "chat", uid })
                  }
                >
                  {t(
                    uid === "imported" ? `${PREFIX}.deleteAllImported` : `${PREFIX}.actions.forget`
                  )}
                </Button>
              )}
            </div>
            {items.map((item) => (
              <div
                key={item.claim_uid}
                className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm break-words whitespace-pre-wrap">{item.text}</p>
                  {item.imported && (
                    <Badge variant="secondary">{t(`${PREFIX}.importedBadge`)}</Badge>
                  )}
                </div>
                <Button
                  aria-label={t("personalization.memoryPanel.actions.deleteMemory")}
                  disabled={remove.isPending}
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirming({ kind: "claim", claim: item })}
                >
                  {t("personalization.memoryPanel.actions.deleteMemory")}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
      {[...localStates]
        .filter(([id]) => !groups.has(id))
        .map(([id]) => (
          <p key={id} role="status">
            {status(id)}
          </p>
        ))}
      {claims.isSuccess && visible.length === 0 && (
        <p className="text-muted-foreground text-sm">{t(`${PREFIX}.claimsEmpty`)}</p>
      )}
      {claims.hasNextPage && (
        <Button
          disabled={claims.isFetchingNextPage}
          variant="outline"
          onClick={() => void claims.fetchNextPage()}
        >
          {t(`${PREFIX}.loadMore`)}
        </Button>
      )}
      <Dialog
        open={confirming !== null}
        onOpenChange={(open) => !open && !remove.isPending && setConfirming(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t(confirmation.title)}</DialogTitle>
            <DialogDescription>{t(confirmation.description)}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              disabled={remove.isPending}
              variant="outline"
              onClick={() => setConfirming(null)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              disabled={remove.isPending}
              variant="destructive"
              onClick={() => confirming && remove.mutate(confirming)}
            >
              {t(confirmation.confirm)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
